import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../store/authSlice';
import api from '../api';

export default function Settings() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Username
  const [username, setUsername] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameSuccess, setUsernameSuccess] = useState('');
  const [usernameError, setUsernameError] = useState('');

  // Security — delete account
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Security — login activity
  const [lastLogin, setLastLogin] = useState(null);
  const [activityLoaded, setActivityLoaded] = useState(false);

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (user?.username) setUsername(user.username);
  }, [user]);

  useEffect(() => {
    api.get('/auth/activity')
      .then(({ data }) => { setLastLogin(data.lastLogin); setActivityLoaded(true); })
      .catch(() => setActivityLoaded(true));
  }, []);

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setUsernameError('');
    setUsernameSuccess('');

    if (!/^[a-zA-Z0-9_]{3,30}$/.test(username)) {
      setUsernameError('3-30 alphanumeric characters or underscores');
      return;
    }

    setUsernameSaving(true);
    try {
      await api.put('/users/username', { username });
      setUsernameSuccess('Username updated');
    } catch (err) {
      setUsernameError(err.response?.data?.error || 'Error updating username');
    } finally {
      setUsernameSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      setPasswordSuccess('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Error changing password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await api.delete('/auth/account', { data: { password: deletePassword } });
      dispatch(logout());
      navigate('/');
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Error deleting account');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const walletAddr = user?.walletAddress || null;

  return (
    <div className="settings-page">
      <h1>Settings</h1>

      {/* Change Username */}
      <div className="settings-section">
        <h2>Change Username</h2>
        <form onSubmit={handleUsernameSubmit}>
          <label className="settings-label">
            Username
            <input
              className="settings-input"
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setUsernameSuccess(''); }}
              placeholder="my_username"
              maxLength={30}
            />
          </label>
          {usernameError && <div className="error-msg">{usernameError}</div>}
          {usernameSuccess && <div className="settings-success">{usernameSuccess}</div>}
          <button type="submit" className="btn btn-primary" disabled={usernameSaving}>
            {usernameSaving ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="settings-section">
        <h2>Change Password</h2>
        <form onSubmit={handlePasswordSubmit}>
          <label className="settings-label">
            Current Password
            <input
              className="settings-input"
              type="password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setPasswordSuccess(''); }}
              required
            />
          </label>
          <label className="settings-label">
            New Password
            <input
              className="settings-input"
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPasswordSuccess(''); }}
              required
            />
          </label>
          <label className="settings-label">
            Confirm New Password
            <input
              className="settings-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setPasswordSuccess(''); }}
              required
            />
          </label>
          {passwordError && <div className="error-msg">{passwordError}</div>}
          {passwordSuccess && <div className="settings-success">{passwordSuccess}</div>}
          <button type="submit" className="btn btn-primary" disabled={passwordSaving}>
            {passwordSaving ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>

      {/* Linked Wallet */}
      <div className="settings-section">
        <h2>Linked Wallet</h2>
        {walletAddr ? (
          <span className="settings-wallet-addr">{walletAddr}</span>
        ) : (
          <span className="settings-no-wallet">No wallet linked</span>
        )}
      </div>

      {/* Security */}
      <div className="settings-section">
        <h2>Security</h2>

        <div className="settings-security-item">
          <span className="settings-label-inline">Last login</span>
          <span className="settings-activity-value">
            {!activityLoaded ? '...' : lastLogin
              ? new Date(lastLogin).toLocaleString()
              : 'No login recorded'}
          </span>
        </div>

        <div className="settings-security-item settings-delete-area">
          {!deleteConfirm ? (
            <button className="btn btn-danger" onClick={() => setDeleteConfirm(true)}>
              Delete Account
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount}>
              <p className="settings-delete-warning">
                Are you sure? This action is permanent and cannot be undone.
              </p>
              <label className="settings-label">
                Enter your password to confirm
                <input
                  className="settings-input"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(''); }}
                  required
                  autoFocus
                />
              </label>
              {deleteError && <div className="error-msg">{deleteError}</div>}
              <div className="settings-delete-buttons">
                <button type="submit" className="btn btn-danger" disabled={deleteLoading}>
                  {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Log Out */}
      <div className="settings-section">
        <button className="btn btn-outline" onClick={handleLogout}>
          Log Out
        </button>
      </div>
    </div>
  );
}
